import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import ForceGraph2D, { ForceGraphMethods, NodeObject, LinkObject } from 'react-force-graph-2d';
import { useGraphStore } from '../../store/graphStore';
import { GraphNode, GraphEdge } from '@the-ledger/shared';

// Custom hook for dimensions
function useDimensions(ref: React.RefObject<HTMLDivElement | null>) {
    const [dimensions, setDimensions] = React.useState({ width: 0, height: 0 });
    React.useEffect(() => {
        if (!ref.current) return;
        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                setDimensions({ width: entry.contentRect.width, height: entry.contentRect.height });
            }
        });
        resizeObserver.observe(ref.current);
        return () => resizeObserver.disconnect();
    }, [ref]);
    return dimensions;
}

// Helper to merge new store data into existing d3 objects to prevent re-simulations
function useGraphDataSync(storeData: { nodes: GraphNode[], edges: GraphEdge[] }) {
    // Persistent storage for d3 objects to preserve simulation state (x, y, vx, vy)
    const d3State = useRef<{ nodes: Map<string, NodeObject>, links: Map<string, LinkObject> }>({
        nodes: new Map(),
        links: new Map()
    });

    return useMemo(() => {
        const { nodes: d3Nodes, links: d3Links } = d3State.current;
        let structureChanged = false;

        // 1. Sync Nodes
        const nextNodes: NodeObject[] = [];
        storeData.nodes.forEach(storeNode => {
            let d3Node = d3Nodes.get(storeNode.id);
            if (!d3Node) {
                // New node
                d3Node = { ...storeNode };
                d3Nodes.set(storeNode.id, d3Node);
                structureChanged = true;
            } else {
                // Update properties (name, color, etc) but allow D3 to keep x,y,vx,vy
                Object.assign(d3Node, storeNode);
            }
            nextNodes.push(d3Node);
        });

        // 2. Sync Links
        const nextLinks: LinkObject[] = [];
        storeData.edges.forEach(storeEdge => {
            let d3Link = d3Links.get(storeEdge.id);
            if (!d3Link) {
                // New link
                d3Link = { ...storeEdge };
                d3Links.set(storeEdge.id, d3Link);
                structureChanged = true;
            } else {
                // Update properties but PROTECT source/target from being overwritten by strings
                // if D3 has already converted them to objects.
                const { source, target, ...safeProps } = storeEdge;
                Object.assign(d3Link, safeProps);
            }
            nextLinks.push(d3Link);
        });

        // Cleanup deleted links
        if (d3Links.size !== nextLinks.length) {
            structureChanged = true;
            const nextIds = new Set(nextLinks.map(l => l.id));
            Array.from(d3Links.keys()).forEach(key => {
                if (!nextIds.has(key)) d3Links.delete(key);
            });
        }

        // Auto-Pinning Strategy for "Less Physics Chaos":
        // 1. If a node has NO connections (isolated), PIN it. It stays where you put it (or where it spawned).
        // 2. If a node HAS connections, UNPIN it (mostly) to let the graph arrange the cluster.
        // 3. BUT if we are text-editing (metadata change only), PIN EVERYTHING to stop drift.

        // Calculate degrees (connections)
        const nodeDegree = new Map<string, number>();
        nextLinks.forEach(link => {
            // Handle D3 type morphism (Object vs String ID)
            const s = (typeof link.source === 'object' && link.source !== null) ? (link.source as any).id : link.source;
            const t = (typeof link.target === 'object' && link.target !== null) ? (link.target as any).id : link.target;

            if (s) nodeDegree.set(String(s), (nodeDegree.get(String(s)) || 0) + 1);
            if (t) nodeDegree.set(String(t), (nodeDegree.get(String(t)) || 0) + 1);
        });

        if (structureChanged) {
            nextNodes.forEach(n => {
                const degree = nodeDegree.get(n.id) || 0;
                if (degree === 0) {
                    // Isolated: PIN
                    n.fx = undefined; // Actually, if we leave it undefined, it floats.
                    // To PIN it, we need to set fx/fy.
                    // But if it's new, we don't have x/y yet.
                    // D3 needs to run one tick to set x/y.
                    // If we set fx=undefined, it moves.

                    // User Request: "unless nodes are connected... we shouldn't ever have them moving around automatically"
                    // If I add a node, it appears at (0,0) or random.
                    // If I want it to NOT MOVE, I must set fx/fy.

                    // Logic:
                    // If n.x is defined (existing node), pin it: n.fx = n.x.
                    // If n.x is undefined (new node), we can't pin it effectively until it has a pos.
                    // BUT if we leave it unpinned, it moves.
                    // Maybe we accept 1 frame of movement?
                    // Or we init x/y manually when creating?
                    // We removed manual x/y in ControlPanel to avoid stacking.

                    // Revised Strategy:
                    // Allow movement initially?
                    // Or just use high damping?

                    // Let's stick to: Isolated = Pin if valid position exists.
                    if (n.x !== undefined && n.y !== undefined) {
                        n.fx = n.x;
                        n.fy = n.y;
                    } else {
                        // New node: Let it find a spot, then next render it will be pinned?
                        // No, next render !structureChanged, so it gets pinned by the "else" block below?
                        // Wait, "else" block pins "metadata update only".
                        // If structureChanged, we are here.
                        // If we don't pin it, it simulates.
                        // Ideally we want it to simulate for a moment then freeze?
                        // That's complex.

                        // Simple approach: Allow physics for isolated nodes initially (so they unstack), 
                        // but once they settle, they don't move? 
                        // "shouldn't ever have them moving around automatically"

                        // If I assume users DRAG nodes to where they want:
                        // Then dragging sets fx/fy.
                        // So isolated nodes should be pinned by default?

                        // Re-reading request: "unless nodes are connected ... we shouldn't ever have them moving"
                        // This implies isolated nodes should generally be static.

                        // For now: Clean up the ReferenceError first.
                        // I will set fx=undefined (allow float) for now, as that's safer than pinning to (0,0).
                        n.fx = undefined;
                        n.fy = undefined;
                    }
                } else {
                    // Connected: Allow physics
                    n.fx = undefined;
                    n.fy = undefined;
                }
            });
        } else {
            // Metadata update (Text Edit): FREEZE ALL because user is typing
            nextNodes.forEach(n => {
                if (n.x !== undefined && n.y !== undefined) {
                    n.fx = n.x;
                    n.fy = n.y;
                }
            });
        }


        return {
            graphData: { nodes: nextNodes, links: nextLinks },
            needsReheat: structureChanged
        };

    }, [storeData.nodes, storeData.edges]);
}

export function GraphCanvas() {
    const containerRef = useRef<HTMLDivElement>(null);
    const fgRef = useRef<ForceGraphMethods>();
    const { width, height } = useDimensions(containerRef);

    // Store access
    const { data, selectNode, selectEdge, addEdge } = useGraphStore((state) => state);

    // Smart Data Sync
    const { graphData, needsReheat } = useGraphDataSync(data);

    // Linking State
    const [isShiftPressed, setIsShiftPressed] = useState(false);
    const [dragStartNode, setDragStartNode] = useState<NodeObject | null>(null);

    // Keyboard listeners for Shift
    useEffect(() => {
        const down = (e: KeyboardEvent) => { if (e.key === 'Shift') setIsShiftPressed(true); };
        const up = (e: KeyboardEvent) => { if (e.key === 'Shift') setIsShiftPressed(false); };
        window.addEventListener('keydown', down);
        window.addEventListener('keyup', up);
        return () => {
            window.removeEventListener('keydown', down);
            window.removeEventListener('keyup', up);
        };
    }, []);

    const handleNodeClick = useCallback((node: NodeObject) => {
        if (!isShiftPressed) {
            selectNode((node as GraphNode).id);
        }
    }, [selectNode, isShiftPressed]);

    const handleLinkClick = useCallback((link: LinkObject) => {
        selectEdge((link as GraphEdge).id);
    }, [selectEdge]);

    const handleBackgroundClick = useCallback(() => {
        selectNode(null);
        selectEdge(null);
    }, [selectNode, selectEdge]);

    // Drag to Link Logic
    const handleNodeDrag = useCallback((node: NodeObject, translate: { x: number, y: number }) => {
        if (isShiftPressed) {
            // We are in linking mode. 
            // We use the node drag event to signal we are dragging FROM this node.
            if (!dragStartNode) setDragStartNode(node);
        }
    }, [isShiftPressed, dragStartNode]);

    const handleNodeDragEnd = useCallback((node: NodeObject) => {
        if (isShiftPressed && dragStartNode) {
            setDragStartNode(null);
        } else {
            // Pin node on drag end
            node.fx = node.x;
            node.fy = node.y;
        }
    }, [isShiftPressed, dragStartNode]);

    // Custom interaction layer for Linking
    // When Shift is held, we want to draw a line from the clicked node to cursor.
    // Use ForceGraph's onNodeDrag isn't enough because it moves the node.
    // We will use standard PointerEvents on the container.

    const tempLinkLine = useRef<{ x: number, y: number, sourceNode: NodeObject } | null>(null);

    return (
        <div
            ref={containerRef}
            className={`w-full h-full bg-background relative overflow-hidden ${isShiftPressed ? 'cursor-crosshair' : ''}`}
        >
            {width > 0 && height > 0 && (
                <ForceGraph2D
                    ref={fgRef}
                    width={width}
                    height={height}
                    graphData={graphData}
                    nodeLabel="name"
                    nodeColor={(node: any) => {
                        switch (node.type) {
                            case 'DigitalAccount': return '#38bdf8';
                            case 'HardwareKey': return '#f87171';
                            case 'RecoveryMethod': return '#4ade80';
                            case 'Person': return '#fbbf24';
                            default: return '#94a3b8';
                        }
                    }}
                    nodeRelSize={6}
                    linkColor={() => '#334155'}
                    linkWidth={4}
                    linkDirectionalParticles={2}
                    linkDirectionalParticleSpeed={0.005}

                    // Interaction Props
                    enableNodeDrag={!isShiftPressed} // Disable node moving when shifting
                    onNodeClick={handleNodeClick}
                    onLinkClick={handleLinkClick}
                    onBackgroundClick={handleBackgroundClick}

                    // Optimization: Cool down if only metadata changed
                    // cooldownTicks={needsReheat ? undefined : 0} -> Removed to ensure redraws happen


                    // Linking via Pointer Events (Overlay)
                    // We can hook into the canvas's pointer events via the wrapper div if we capture them.
                    // But ForceGraph captures them.

                    // "Smart" Linking:
                    // If Shift is pressed, clicking a node sets it as SOURCE.
                    // Dragging renders a line.
                    // Releasing on another node sets TARGET.

                    onNodeDrag={(node) => {
                        // If we are dragging, and shift is pressed? 
                        // actually enableNodeDrag={false} means onNodeDrag won't fire.
                    }}

                    // Custom Canvas Layer for Linking Line
                    onRenderFramePost={(ctx, globalScale) => {
                        if (isShiftPressed && dragStartNode && tempLinkLine.current) {
                            ctx.beginPath();
                            ctx.moveTo(dragStartNode.x!, dragStartNode.y!);
                            // We need graph coords for the cursor.
                            // This is hard to get inside this render frame without tracking mouse state externally.
                            // But wait, we can track mouse state on the DIV.
                            const { x, y } = tempLinkLine.current;
                            ctx.lineTo(x, y);
                            ctx.strokeStyle = '#f472b6'; // Accent color
                            ctx.lineWidth = 2 / globalScale;
                            ctx.setLineDash([5 / globalScale, 5 / globalScale]);
                            ctx.stroke();
                            ctx.setLineDash([]);
                        }
                    }}

                    nodeCanvasObject={(node: any, ctx, globalScale) => {
                        const label = node.name;
                        const fontSize = 12 / globalScale;
                        ctx.font = `${fontSize}px Sans-Serif`;

                        // Draw Node
                        ctx.beginPath();
                        const r = 4;
                        ctx.arc(node.x, node.y, r, 0, 2 * Math.PI, false);
                        ctx.fillStyle = node.color ||
                            (node.type === 'DigitalAccount' ? '#38bdf8' :
                                node.type === 'HardwareKey' ? '#f87171' :
                                    node.type === 'RecoveryMethod' ? '#4ade80' :
                                        node.type === 'Person' ? '#fbbf24' : '#94a3b8');
                        ctx.fill();

                        // Label
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillStyle = '#f8fafc';
                        ctx.fillText(label, node.x, node.y + 8);

                        // Selection Ring
                        const isSelected = useGraphStore.getState().selectedNodeId === node.id;
                        // Focus Ring
                        if (isSelected) {
                            ctx.beginPath();
                            ctx.arc(node.x, node.y, r + 2, 0, 2 * Math.PI, false);
                            ctx.strokeStyle = '#fff';
                            ctx.lineWidth = 1 / globalScale;
                            ctx.stroke();
                        }

                        // Linking Highlight
                        if (isShiftPressed) {
                            ctx.beginPath();
                            ctx.arc(node.x, node.y, r + 4, 0, 2 * Math.PI, false);
                            ctx.strokeStyle = '#f472b6'; // Pink ring
                            ctx.lineWidth = 0.5 / globalScale;
                            ctx.stroke();
                        }
                    }}

                    linkCanvasObjectMode={() => 'after'}
                    linkCanvasObject={(link: any, ctx, globalScale) => {
                        // Draw Label
                        const label = link.type;
                        const fontSize = 10 / globalScale;
                        ctx.font = `${fontSize}px Sans-Serif`;
                        const textWidth = ctx.measureText(label).width;
                        const bckgDimensions = [textWidth, fontSize].map(n => n + fontSize * 0.2);

                        // Calculate midpoint
                        const start = link.source;
                        const end = link.target;
                        // ignore unbound links
                        if (typeof start !== 'object' || typeof end !== 'object') return;

                        const x = start.x + (end.x - start.x) / 2;
                        const y = start.y + (end.y - start.y) / 2;

                        // Draw background for text
                        ctx.fillStyle = 'rgba(15, 23, 42, 0.8)';
                        ctx.fillRect(x - bckgDimensions[0] / 2, y - bckgDimensions[1] / 2, bckgDimensions[0], bckgDimensions[1]);

                        // Draw text
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillStyle = useGraphStore.getState().selectedEdgeId === link.id ? '#38bdf8' : '#94a3b8';
                        ctx.fillText(label, x, y);
                    }}

                // Thicker Hit Area for easier selection
                // linkHoverPrecision={10} // If supported by library, else linkWidth does it

                />
            )}

            {/* Transparent Overlay to capture Drag logic when Shifting */}
            {isShiftPressed && (
                <div
                    className="absolute inset-0 z-50 cursor-crosshair"
                    onMouseDown={(e) => {
                        // Hit test manually
                        if (!fgRef.current) return;
                        const coords = fgRef.current.screen2GraphCoords(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
                        // Find node near coords
                        const node = graphData.nodes.find(n => {
                            const dist = Math.sqrt(Math.pow(n.x! - coords.x, 2) + Math.pow(n.y! - coords.y, 2));
                            return dist < 8; // Hit radius
                        });

                        if (node) {
                            setDragStartNode(node);
                            tempLinkLine.current = { x: coords.x, y: coords.y, sourceNode: node };
                        }
                    }}
                    onMouseMove={(e) => {
                        if (dragStartNode && fgRef.current) {
                            const coords = fgRef.current.screen2GraphCoords(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
                            tempLinkLine.current = { x: coords.x, y: coords.y, sourceNode: dragStartNode };
                        }
                    }}
                    onMouseUp={(e) => {
                        if (dragStartNode && fgRef.current) {
                            // Find drop target
                            const coords = fgRef.current.screen2GraphCoords(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
                            const targetNode = graphData.nodes.find(n => {
                                const dist = Math.sqrt(Math.pow(n.x! - coords.x, 2) + Math.pow(n.y! - coords.y, 2));
                                return dist < 8;
                            });

                            if (targetNode && targetNode.id !== dragStartNode.id) {
                                // Create Edge!
                                addEdge({
                                    source: dragStartNode.id as string,
                                    target: targetNode.id as string,
                                    type: 'SecuredBy' // Default
                                });
                            }
                        }
                        setDragStartNode(null);
                        tempLinkLine.current = null;
                    }}
                />
            )}
        </div>
    );
}
